import React, { useState, useEffect } from 'react';
import { X, MapPin } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { fetchGasInfraByPipeline, fetchPipelineFiberLookup, fetchPipelineObservations, type ConnectedFacility, type PipelineFiberInfo, type PipelineObservationsResponse } from '../lib/api';
import { Section, Field } from './education/Section';
import { PipelineScenario } from './PipelineScenario';
import { pipelineCapacityDisplay, type PipelineCapacityMetadata } from '../lib/pipeline-capacity';

export interface PipelineProperties extends PipelineCapacityMetadata {
  id: string;
  name: string;
  operator: string;
  type: string;
  source: string;
  diameter_inches: number | null;
  capacity_mmcf_day: number | null;
  mw_potential: number | null;
  capacity_source: string | null;
  utilization_pct: number | null;
  utilization_source: string | null;
  flow_volume_mmcf_day: number | null;
}

interface PipelineDetailProps {
  pipeline: PipelineProperties;
  // Owning company from the pipeline company registry, or a JV label naming the operator.
  company?: string | null;
  onClose: () => void;
  onZoomToFacility?: (lng: number, lat: number) => void;
}

const FACILITY_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  compressor: { label: 'Compressor Stations', color: designTokens.colors.gasInfraCompressor },
  receipt_delivery: { label: 'Receipt/Delivery Points', color: designTokens.colors.gasInfraReceiptDelivery },
  interconnect: { label: 'Interconnects', color: designTokens.colors.gasInfraInterconnect },
  gas_storage: { label: 'Gas Storage', color: designTokens.colors.gasInfraStorage },
};

export const PipelineDetail: React.FC<PipelineDetailProps> = ({ pipeline, company, onClose, onZoomToFacility }) => {
  const { theme } = useTheme();
  const [facilities, setFacilities] = useState<ConnectedFacility[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState(false);
  const [fiberInfo, setFiberInfo] = useState<PipelineFiberInfo | null>(null);
  const [observations, setObservations] = useState<PipelineObservationsResponse | null>(null);
  const [observationsLoading, setObservationsLoading] = useState(false);
  const [observationsError, setObservationsError] = useState(false);

  const capacity = pipelineCapacityDisplay(pipeline);
  const isAnnounced = capacity.basis === 'operator_announced';

  useEffect(() => {
    setObservations(null);
    setObservationsError(false);
    setObservationsLoading(true);
    const controller = new AbortController();
    fetchPipelineObservations(pipeline.id, controller.signal)
      .then(data => { if (!controller.signal.aborted) setObservations(data); })
      .catch(() => { if (!controller.signal.aborted) { setObservations(null); setObservationsError(true); } })
      .finally(() => { if (!controller.signal.aborted) setObservationsLoading(false); });
    return () => controller.abort();
  }, [pipeline.id]);

  useEffect(() => {
    setFacilities([]);
    setFacilitiesError(false);
    setFacilitiesLoading(false);
    if (isAnnounced) return;
    const controller = new AbortController();
    setFacilitiesLoading(true);
    fetchGasInfraByPipeline(pipeline.id, controller.signal)
      .then(data => { if (!controller.signal.aborted) setFacilities(data); })
      .catch(err => {
        if (!controller.signal.aborted) {
          setFacilitiesError(true);
          console.error('Failed to load facilities:', err);
        }
      })
      .finally(() => { if (!controller.signal.aborted) setFacilitiesLoading(false); });
    return () => controller.abort();
  }, [pipeline.id, isAnnounced]);

  // Fiber lookup: does this pipeline's operator match a known fiber-ROW operator?
  useEffect(() => {
    if (!pipeline.operator) { setFiberInfo(null); return; }
    const controller = new AbortController();
    fetchPipelineFiberLookup(pipeline.operator, controller.signal)
      .then(info => { if (!controller.signal.aborted) setFiberInfo(info); })
      .catch(() => { /* 404 handled inside helper */ });
    return () => controller.abort();
  }, [pipeline.operator]);
  const p = pipeline;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Pipeline Details</h2>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {isAnnounced && (
          <div style={{ padding: '8px 12px', marginBottom: '12px', borderRadius: '6px', background: '#7c3aed15', fontSize: '11px', color: '#7c3aed' }}>
            Announced project — operating status and in-service date unverified
          </div>
        )}

        {/* Pipeline identity */}
        <Section title="Pipeline">
          <Field label="Name" value={p.name || 'Unknown'} />
          <Field label="Operator" value={p.operator} />
          <Field label="Company" value={company} />
          <Field label="Type" value={p.type} />
          <Field label="Data Source" value={p.source === 'EIA' ? 'EIA pipeline geometry' : p.source === 'RRC' ? 'TX RRC' : p.source} />
        </Section>

        <Section title="Capacity">
          <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '6px' }}>{capacity.label}</div>
          {capacity.capacityMmcf != null && (
            <Field label="Gas throughput" value={`${capacity.capacityMmcf.toLocaleString(undefined, { maximumFractionDigits: 0 })} MMcf/d`} />
          )}
          <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{capacity.explanation}</div>
          {capacity.sourceCapacityMmcf != null && (
            <div style={{ marginTop: '10px', paddingTop: '6px', borderTop: `1px solid ${theme.borderLight}` }}>
              <Field label={capacity.sourceLabel} value={`${capacity.sourceCapacityMmcf.toLocaleString()} MMcf/d`} />
              <div style={{ fontSize: '10px', color: theme.textTertiary, lineHeight: 1.5 }}>
                Source context only; this does not establish delivery capacity at the selected segment.
              </div>
            </div>
          )}
        </Section>

        {!/carbon dioxide|crude|liquid|ammonia|hydrogen|nitrogen/i.test(p.type || '') && (
          <PipelineScenario key={p.id} recordedDiameter={p.diameter_inches} />
        )}

        <Section title="Operator-posted observations">
          {observationsLoading ? (
            <div style={{ fontSize: '11px', color: theme.textTertiary }}>Loading…</div>
          ) : observations?.observations?.length ? (
            <>
              <div style={{ fontSize: '10px', color: theme.textSecondary, lineHeight: 1.5, marginBottom: '8px' }}>
                Showing {Math.min(observations.observations.length, 8)} of {observations.coverage.observationCount} operator-posted rows for the same pipeline system. These postings do not establish flow, local segment capacity, or capacity available to a new shipper. These records may not cover the complete operator report.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <th style={{ textAlign: 'left', padding: '4px 2px', color: theme.textSecondary, fontWeight: 600 }}>Segment / gas day</th>
                      <th style={{ textAlign: 'right', padding: '4px 2px', color: theme.textSecondary, fontWeight: 600 }}>Operating</th>
                      <th style={{ textAlign: 'right', padding: '4px 2px', color: theme.textSecondary, fontWeight: 600 }}>Scheduled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {observations.observations.slice(0, 8).map((row, index) => (
                      <tr key={`${row.id}-${index}`} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                        <td style={{ padding: '4px 2px', color: theme.text }}>
                          <div>{row.segmentName || row.segmentNumber || row.pipelineCode || 'System total'}</div>
                          <div style={{ color: theme.textTertiary }}>{row.gasDay || '—'}{row.cycle ? ` · ${row.cycle}` : ''}{isStaleGasDay(row.gasDay) ? ' · stale' : ''}</div>
                        </td>
                        <td style={{ padding: '4px 2px', textAlign: 'right', color: theme.text, fontFamily: designTokens.typography.fontFamily.mono }}>{formatDth(row.operatingCapacityDth)}</td>
                        <td style={{ padding: '4px 2px', textAlign: 'right', color: theme.text, fontFamily: designTokens.typography.fontFamily.mono }}>{formatDth(row.scheduledQuantityDth)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '9px', color: theme.textTertiary, lineHeight: 1.5, marginTop: '7px' }}>
                Scope: same pipeline system · native units: Dth/d · source: {observations.observations[0].source || 'operator posting'}
                {observations.observations.find(row => row.sourceUrl)?.sourceUrl && (
                  <> · <a href={observations.observations.find(row => row.sourceUrl)?.sourceUrl || '#'} target="_blank" rel="noreferrer" style={{ color: theme.link }}>Source link</a></>
                )}
              </div>
            </>
          ) : observationsError ? (
            <div style={{ fontSize: '11px', color: theme.textSecondary }}>Operator observation request failed. Retry when the pipeline service is available.</div>
          ) : (
            <div style={{ fontSize: '11px', color: theme.textTertiary, fontStyle: 'italic' }}>No operator-posted observations available.</div>
          )}
        </Section>

        {/* Physical */}
        <Section title="Physical">
          <Field label="Diameter" value={p.diameter_inches ? `${p.diameter_inches}\u2033` : null} />
        </Section>

        {/* Fiber Infrastructure — shown only if operator has a documented fiber-ROW business */}
        {fiberInfo && (
          <Section title="Fiber Infrastructure">
            <div style={{
              padding: '8px 10px', marginBottom: '8px', borderRadius: '6px',
              background: '#7c3aed15', border: '1px solid #7c3aed40',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
                  background: '#7c3aed', flexShrink: 0,
                }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#7c3aed' }}>
                  Dark fiber in pipeline ROW
                </span>
                <span style={{
                  marginLeft: 'auto', fontSize: '9px', padding: '1px 6px',
                  background: '#7c3aed20', color: '#7c3aed', borderRadius: '3px', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  {fiberInfo.fiber_type}
                </span>
              </div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: theme.text, marginBottom: '4px' }}>
                {fiberInfo.fiber_company}
              </div>
              <div style={{ fontSize: '10px', color: theme.textSecondary, lineHeight: 1.5 }}>
                {fiberInfo.note}
              </div>
            </div>
            <div style={{ fontSize: '9px', color: theme.textTertiary }}>
              Lease availability is not guaranteed — contact operator wholesale for current inventory and pricing.
            </div>
          </Section>
        )}

        {/* Nearby Gas Infrastructure */}
        <Section title={`Nearby Facilities${facilities.length > 0 ? ` (${facilities.length})` : ''}`}>
          {facilitiesLoading ? (
            <div style={{ fontSize: '11px', color: theme.textTertiary }}>Loading...</div>
          ) : isAnnounced ? (
            <div style={{ fontSize: '11px', color: theme.textTertiary, fontStyle: 'italic' }}>
              Facility availability requires confirmation of project operating status
            </div>
          ) : facilitiesError ? (
            <div style={{ fontSize: '11px', color: theme.textTertiary }}>
              Nearby facilities could not be loaded
            </div>
          ) : facilities.length === 0 ? (
            <div style={{ fontSize: '11px', color: theme.textTertiary, fontStyle: 'italic' }}>
              No gas infrastructure found near this pipeline segment
            </div>
          ) : (
            <>
              {Object.entries(
                facilities.reduce<Record<string, ConnectedFacility[]>>((acc, f) => {
                  (acc[f.type] = acc[f.type] || []).push(f);
                  return acc;
                }, {})
              ).map(([type, items]) => {
                const cfg = FACILITY_TYPE_CONFIG[type] || { label: type, color: '#6b7280' };
                return (
                  <div key={type} style={{ marginBottom: '8px' }}>
                    <div style={{
                      fontSize: '10px', fontWeight: 600, color: cfg.color,
                      marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>
                      {cfg.label} ({items.length})
                    </div>
                    {items.map(f => (
                      <button
                        key={f.id}
                        onClick={() => onZoomToFacility?.(f.lng, f.lat)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                          padding: '4px 6px', marginBottom: '2px', background: 'none',
                          border: `1px solid transparent`, borderRadius: '4px', cursor: 'pointer',
                          textAlign: 'left', fontSize: '11px', color: theme.text,
                          transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = theme.border)}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = 'transparent')}
                      >
                        <span style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          background: cfg.color, flexShrink: 0,
                        }} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.name || 'Unnamed'}
                          {f.state && (
                            <span style={{ fontSize: '9px', color: theme.textTertiary, marginLeft: '4px' }}>{f.state}</span>
                          )}
                        </span>
                        {f.horsepower && (
                          <span style={{
                            fontSize: '10px', color: theme.textTertiary, flexShrink: 0,
                            fontFamily: designTokens.typography.fontFamily.mono,
                          }}>
                            {f.horsepower.toLocaleString()} HP
                          </span>
                        )}
                        {!f.horsepower && f.capacity_dth_day && (
                          <span style={{
                            fontSize: '10px', color: theme.textTertiary, flexShrink: 0,
                            fontFamily: designTokens.typography.fontFamily.mono,
                          }}>
                            {f.capacity_dth_day.toLocaleString()} Dth/d
                          </span>
                        )}
                        <MapPin size={10} style={{ color: theme.textTertiary, flexShrink: 0 }} />
                      </button>
                    ))}
                  </div>
                );
              })}
              <div style={{ fontSize: '9px', color: theme.textTertiary, marginTop: '4px' }}>
                Within 3 km of this segment when names match, otherwise within 500 m.
                {' '}Proximity does not confirm a physical connection.
              </div>
            </>
          )}
        </Section>
      </div>
    </div>
  );
};

function formatDth(value: number | null): string {
  return value == null ? '—' : `${value.toLocaleString()} Dth/d`;
}

function isStaleGasDay(value: string | null): boolean {
  if (!value) return false;
  const day = new Date(`${value.slice(0, 10)}T00:00:00Z`).getTime();
  if (!Number.isFinite(day)) return false;
  return Date.now() - day > 7 * 24 * 60 * 60 * 1000;
}
